import { useState } from "react";
import { History } from "lucide-react";
import { formatISTDate } from "../../lib/datetime";
import { joinParts, plural } from "../../lib/vocabulary";

/**
 * The PL OPENING COMPONENT and who put it there — ONE implementation (D-004).
 *
 * PL accrues only from the accrual epoch, because days on duty before the app
 * existed were never observed. Pre-epoch service is honoured by a figure the
 * Director enters per person, and D-004 requires that figure to be "VISIBLE AND
 * ATTRIBUTABLE — never folded silently into accrued", showing "on the
 * employee's OWN balance view (not just the Director's)". So this component
 * renders on both, from the same `PL` object the backend serves.
 *
 * THREE STATES, NEVER TWO.
 *   entered      — the figure, plus who entered it, when and why.
 *   not entered  — said as a gap. Before go-live this is everybody, and it is
 *                  the state the Director is working through; rendering it as
 *                  "0 days opening" would report a decision nobody made.
 *   not reported — the response did not carry `opening_entered` at all, which
 *                  is not the same as "no". Missing stays missing (Pattern A).
 *
 * A CORRECTION IS AN AMENDMENT, so the history is a list. The latest entry is
 * the figure in force; the ones before it are what it replaced, and they are
 * shown on request rather than hidden, because "why is my opening 11 and not
 * 8" is exactly the question this trail exists to answer.
 */
export default function OpeningBalanceNote({ pl, testId }) {
  const [showHistory, setShowHistory] = useState(false);
  if (!pl) return null;

  const reported = pl.opening_entered !== null && pl.opening_entered !== undefined;
  if (!reported) {
    return (
      <p className="mt-1 text-[11px] text-callisto-muted" data-testid={`${testId}-unknown`}>
        Opening balance not reported
      </p>
    );
  }

  if (!pl.opening_entered) {
    return (
      <p className="mt-1 text-[11px] text-callisto-muted" data-testid={`${testId}-none`}>
        No opening balance entered — PL shown here is what has accrued since the
        scheme started.
      </p>
    );
  }

  const history = pl.opening_history || [];
  const latest = history.length ? history[history.length - 1] : null;

  return (
    <div className="mt-1" data-testid={testId}>
      <p className="text-[11px] text-callisto-body">
        Includes {plural(pl.opening, "day")} opening balance
        {latest && (
          <span className="text-callisto-muted">
            {" "}
            — {joinParts([latest.actor_name, formatISTDate(latest.created_at)], " on ")}
          </span>
        )}
      </p>
      {latest?.reason && (
        <p className="text-[11px] text-callisto-muted" data-testid={`${testId}-reason`}>
          “{latest.reason}”
        </p>
      )}
      {history.length > 1 && (
        <>
          <button
            type="button"
            onClick={() => setShowHistory((v) => !v)}
            className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-semibold text-callisto-navy underline"
            data-testid={`${testId}-history-toggle`}
            aria-expanded={showHistory}
          >
            <History size={11} />
            {showHistory ? "Hide" : "Show"} {history.length - 1} earlier{" "}
            {history.length === 2 ? "figure" : "figures"}
          </button>
          {showHistory && (
            <ul className="mt-1 space-y-0.5" data-testid={`${testId}-history`}>
              {history
                .slice(0, -1)
                .reverse()
                .map((entry) => (
                  <li
                    key={entry.id}
                    className="text-[11px] text-callisto-muted"
                    data-testid={`${testId}-history-${entry.id}`}
                  >
                    {joinParts([
                      plural(entry.opening, "day"),
                      entry.actor_name,
                      formatISTDate(entry.created_at),
                      entry.reason && `“${entry.reason}”`,
                    ])}
                  </li>
                ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
